import React, { useState } from 'react';
import { 
  Cpu, 
  Truck, 
  Package, 
  ShieldCheck, 
  Zap, 
  ArrowRight, 
  CloudLightning,
  Lock,
  FileCheck,
  BarChart4,
  Workflow,
  Globe,
  MapPin,
  Clock,
  Users,
  Database
} from 'lucide-react';
import Header from './Header';


const BlueGeckoFalcon360Logistics = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [activeFeature, setActiveFeature] = useState(null);
  const [activeBenefit, setActiveBenefit] = useState(null);

  // Key features of Falcon360 for Logistics
  const keyFeatures = [
    {
      icon: <Database className="text-blue-400" size={32} />,
      title: "End-to-End System Migration",
      description: "Comprehensive mapping from legacy WMS/TMS systems to modern platforms with automated data extraction and transformation"
    },
    {
      icon: <CloudLightning className="text-purple-400" size={32} />,
      title: "Multi-Format Data Integration",
      description: "Support for EDI, API, CSV, and custom data formats with intelligent categorization for inventory, orders, and routes"
    },
    {
      icon: <Workflow className="text-green-400" size={32} />,
      title: "Regulatory Compliance Preservation",
      description: "Maintains historical audit trails and ensures customs and taxation data integrity for compliance reporting"
    },
    {
      icon: <FileCheck className="text-teal-400" size={32} />,
      title: "Real-Time Migration Capabilities",
      description: "Minimizes downtime with phased migration approaches and real-time validation during the migration process"
    }
  ];

  // Business benefits
  const keyBenefits = [
    {
      icon: <Zap className="text-yellow-400" size={32} />,
      title: "Operational Efficiency Gains",
      description: "Reduce manual data entry by up to 75% and eliminate data silos for improved cross-functional collaboration"
    },
    {
      icon: <ShieldCheck className="text-red-400" size={32} />,
      title: "Enhanced Customer Service",
      description: "Improved delivery tracking accuracy and faster issue resolution through integrated customer and shipment data"
    },
    {
      icon: <Lock className="text-indigo-400" size={32} />,
      title: "Risk Mitigation",
      description: "Improved regulatory compliance and reduced data loss risk during system transitions with complete audit trails"
    },
    {
      icon: <Globe className="text-emerald-400" size={32} />,
      title: "Competitive Advantage",
      description: "Faster time-to-market for new services and advanced analytics capabilities for data-driven decision making"
    }
  ];

  const complianceStandards = [
    { name: "Customs Compliance", color: "bg-blue-500" },
    { name: "DOT Regulations", color: "bg-purple-500" },
    { name: "CTPAT", color: "bg-green-500" },
    { name: "HAZMAT", color: "bg-yellow-500" },
    { name: "Cross-Border", color: "bg-red-500" }
  ];

  return (
    <div className="relative bg-gradient-to-b from-gray-900 via-gray-900 to-indigo-900 min-h-screen text-white overflow-hidden">
      <Header />
      <div
        className="relative bg-gradient-to-b from-transparent via-indigo-900 to-indigo-900 min-h-screen text-white overflow-hidden"
        style={{ backgroundColor: 'rgba(2, 6, 23, 0.9)' }}
      >
        {/* Animated Background Particles */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          {Array.from({ length: 100 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-indigo-500"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 5 + 2}px`,
                height: `${Math.random() * 5 + 2}px`,
                opacity: Math.random() * 0.5,
                animation: `float ${Math.random() * 20 + 10}s linear infinite`,
                animationDelay: `${Math.random() * 10}s`
              }}
            />
          ))}
        </div>

        <div className="max-w-6xl mx-auto px-4 py-12 relative">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="bg-indigo-600 bg-opacity-30 p-3 rounded-full">
                <Truck size={40} className="text-indigo-300 animate-pulse" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-400 to-purple-300 bg-clip-text text-transparent">
                  BlueGecko Falcon Mapping
                </h1>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <p className="text-indigo-200">Intelligent Data Migration for Logistics & Supply Chain</p>
                </div>
              </div>
            </div>
            <div className="bg-indigo-900 bg-opacity-30 backdrop-blur-sm w-fit mx-auto px-6 py-2 rounded-full border border-indigo-500 border-opacity-30 mb-8">
              <span className="text-indigo-200">Powered by Advanced GenAI Technology</span>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex justify-center mb-12">
            {[
              { key: 'overview', label: 'Why It Matters in Logistics' },
              { key: 'features', label: 'How Falcon Mapping Supports' },
              { key: 'benefits', label: 'Business Outcomes' }
            ].map((tab) => (
              <button
                key={tab.key}
                className={`
                  px-6 py-3 mx-2 rounded-lg transition-all duration-300
                  ${activeSection === tab.key 
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-900/50' 
                    : 'bg-gray-800 bg-opacity-50 text-indigo-200 hover:bg-gray-700 hover:bg-opacity-50'}
                `}
                onClick={() => setActiveSection(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Sections */}
          {activeSection === 'overview' && (
            <div className="bg-gray-900 bg-opacity-50 backdrop-blur-lg rounded-2xl p-8 border border-indigo-500 border-opacity-30 shadow-xl">
              <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-indigo-300 to-purple-300 bg-clip-text text-transparent flex items-center">
                <Truck size={24} className="mr-2 text-indigo-400" />
                Why It Matters in Logistics
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4 bg-gray-800 bg-opacity-50 p-5 rounded-xl border border-indigo-800 border-opacity-30 hover:border-indigo-500 hover:border-opacity-50 transition-all duration-300">
                  <div className="min-w-8 mt-1 text-indigo-400"><Package size={20} /></div>
                  <div>
                    <h3 className="text-indigo-200 font-medium mb-2">Complex System Integration Needs</h3>
                    <p className="text-gray-300">Logistics companies operate multiple interconnected systems (WMS, TMS, ERP, CRM) with years of valuable historical data that must be preserved</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 bg-gray-800 bg-opacity-50 p-5 rounded-xl border border-indigo-800 border-opacity-30 hover:border-indigo-500 hover:border-opacity-50 transition-all duration-300">
                  <div className="min-w-8 mt-1 text-indigo-400"><Clock size={20} /></div>
                  <div>
                    <h3 className="text-indigo-200 font-medium mb-2">Real-Time Decision Making Requirements</h3>
                    <p className="text-gray-300">Route optimization, inventory management, and fleet management decisions need instant access to accurate, up-to-date data</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 bg-gray-800 bg-opacity-50 p-5 rounded-xl border border-indigo-800 border-opacity-30 hover:border-indigo-500 hover:border-opacity-50 transition-all duration-300">
                  <div className="min-w-8 mt-1 text-indigo-400"><ShieldCheck size={20} /></div>
                  <div>
                    <h3 className="text-indigo-200 font-medium mb-2">Regulatory Compliance Demands</h3>
                    <p className="text-gray-300">Customs documentation, taxation records, and transportation regulations require accurate tracking and reporting capabilities</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 bg-gray-800 bg-opacity-50 p-5 rounded-xl border border-indigo-800 border-opacity-30 hover:border-indigo-500 hover:border-opacity-50 transition-all duration-300">
                  <div className="min-w-8 mt-1 text-indigo-400"><Users size={20} /></div>
                  <div>
                    <h3 className="text-indigo-200 font-medium mb-2">Customer Experience Expectations</h3>
                    <p className="text-gray-300">End-to-end shipment tracking visibility and service continuity during system transitions are critical for maintaining customer relationships</p>
                  </div>
                </div>
              </div>
              
              {/* Compliance Standards */}
              <div className="mt-8">
                <h3 className="text-xl font-semibold text-indigo-300 mb-4">Regulatory Standards Supported</h3>
                <div className="flex flex-wrap gap-3">
                  {complianceStandards.map((standard) => (
                      <div key={standard.name} className="bg-gray-800 bg-opacity-40 px-4 py-2 rounded-full flex items-center border border-gray-700">
                        <div className={`w-3 h-3 ${standard.color} rounded-full mr-2`}></div>
                        <span className="text-gray-200">{standard.name}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'features' && (
            <div className="space-y-8">
              <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-indigo-300 to-purple-300 bg-clip-text text-transparent text-center flex items-center justify-center">
                <Cpu size={24} className="mr-2 text-indigo-400" />
                How Falcon Mapping Supports Logistics
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {keyFeatures.map((feature) => (
                  <button 
                    key={feature.title}
                    className={`
                      p-6 rounded-xl transition-all duration-300 text-left
                      ${activeFeature === feature.title 
                        ? 'bg-gray-800 border-2 border-indigo-500 shadow-2xl' 
                        : 'bg-gray-900 bg-opacity-50 border border-indigo-800 border-opacity-30'}
                    `}
                    onMouseEnter={() => setActiveFeature(feature.title)}
                    onMouseLeave={() => setActiveFeature(null)}
                    onFocus={() => setActiveFeature(feature.title)}
                    onBlur={() => setActiveFeature(null)}
                  >
                    <div className="flex items-center mb-4">
                      <div className="bg-indigo-900 bg-opacity-30 p-3 rounded-full">
                        {feature.icon}
                      </div>
                      <h3 className="ml-4 text-xl font-semibold text-indigo-200">
                        {feature.title}
                      </h3>
                    </div>
                    <p className="text-gray-300">
                      {feature.description}
                    </p>
                  </button>
                ))}
              </div>
              
              <div className="bg-gray-900 bg-opacity-50 backdrop-blur-lg rounded-xl p-6 border border-indigo-500 border-opacity-30">
                <h3 className="text-xl font-semibold text-indigo-300 mb-4">Key Logistics Systems Supported</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-800 bg-opacity-50 p-4 rounded-lg border border-indigo-800 border-opacity-30 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-indigo-200">SAP WMS</span>
                    </div>
                  </div>
                  <div className="bg-gray-800 bg-opacity-50 p-4 rounded-lg border border-indigo-800 border-opacity-30 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                      <span className="text-indigo-200">Oracle TMS</span>
                    </div>
                  </div>
                  <div className="bg-gray-800 bg-opacity-50 p-4 rounded-lg border border-indigo-800 border-opacity-30 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-indigo-200">Manhattan WMS</span>
                    </div>
                  </div>
                  <div className="bg-gray-800 bg-opacity-50 p-4 rounded-lg border border-indigo-800 border-opacity-30 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                      <span className="text-indigo-200">JDA/Blue Yonder</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-indigo-900 bg-opacity-30 p-6 rounded-xl border border-indigo-500 border-opacity-20">
                <div className="flex items-start gap-4">
                  <div className="bg-indigo-800 bg-opacity-30 p-3 rounded-full mt-1">
                    <MapPin size={24} className="text-indigo-300" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-indigo-200 mb-2">Logistics-Specific AI Capabilities</h3>
                    <p className="text-gray-300">Falcon Mapping's specialized AI understands the complexities of supply chain data structures, route optimization algorithms, and inventory management systems to ensure precise migrations with contextual awareness of logistics operations.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'benefits' && (
            <div className="space-y-8">
              <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-indigo-300 to-purple-300 bg-clip-text text-transparent text-center flex items-center justify-center">
                <BarChart4 size={24} className="mr-2 text-indigo-400" />
                Business Outcomes for Logistics
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {keyBenefits.map((benefit) => (
                  <button 
                    key={benefit.title}
                    className={`
                      p-6 rounded-xl transition-all duration-300 relative overflow-hidden text-left
                      ${activeBenefit === benefit.title 
                        ? 'bg-gradient-to-br from-indigo-900 to-indigo-800 border-2 border-indigo-500 shadow-2xl' 
                        : 'bg-gray-900 bg-opacity-50 border border-indigo-800 border-opacity-30'}
                    `}
                    onMouseEnter={() => setActiveBenefit(benefit.title)}
                    onMouseLeave={() => setActiveBenefit(null)}
                    onFocus={() => setActiveBenefit(benefit.title)}
                    onBlur={() => setActiveBenefit(null)}
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500 opacity-10 rounded-full blur-xl"></div>
                    <div className="flex items-center mb-4">
                      <div className="bg-indigo-900 bg-opacity-30 p-3 rounded-full">
                        {benefit.icon}
                      </div>
                      <h4 className="ml-4 text-xl font-semibold text-indigo-200">
                        {benefit.title}
                      </h4>
                    </div>
                    <p className="text-gray-300 relative z-10">
                      {benefit.description}
                    </p>
                  </button>
                ))}
              </div>
              
              <div className="bg-gradient-to-r from-indigo-900 to-purple-900 p-8 rounded-xl border border-indigo-500 border-opacity-30 text-center">
                <h3 className="text-2xl font-bold text-white mb-4">Ready to Transform Your Logistics Operations?</h3>
                <p className="text-indigo-200 mb-6">
                  Experience the power of AI-driven migration with BlueGecko Falcon Mapping and accelerate your logistics digital transformation.
                </p>
                <button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold py-4 px-10 rounded-lg transition-all duration-300 flex items-center justify-center mx-auto group">
                  Schedule a Demo
                  <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" size={20} />
                </button>
              </div>
            </div>
          )}

          {/* Cost Savings Visualization */}
          {activeSection === 'benefits' && (
            <div className="mt-12 bg-gray-900 bg-opacity-50 backdrop-blur-lg rounded-xl p-8 border border-indigo-500 border-opacity-30">
              <h3 className="text-xl font-bold text-indigo-300 mb-6">Cost Optimization Impact</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="bg-gradient-to-r from-green-600 to-green-400 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-white text-xl font-bold">75%</span>
                  </div>
                  <h4 className="text-green-300 font-semibold mb-2">Manual Processing Reduction</h4>
                  <p className="text-gray-300 text-sm">Automated data entry and processing</p>
                </div>
                <div className="text-center">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-400 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-white text-xl font-bold">60%</span>
                  </div>
                  <h4 className="text-blue-300 font-semibold mb-2">Infrastructure Cost Savings</h4>
                  <p className="text-gray-300 text-sm">Cloud migration and system consolidation</p>
                </div>
                <div className="text-center">
                  <div className="bg-gradient-to-r from-purple-600 to-purple-400 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-white text-xl font-bold">50%</span>
                  </div>
                  <h4 className="text-purple-300 font-semibold mb-2">Faster Time-to-Market</h4>
                  <p className="text-gray-300 text-sm">Accelerated deployment of new services</p>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-12 text-center">
            <div className="inline-block bg-indigo-900 bg-opacity-30 px-4 py-2 rounded-full text-xs flex items-center gap-2 border border-indigo-500 border-opacity-20">
              <span className="h-2 w-2 bg-indigo-400 rounded-full animate-pulse"></span>
              <span className="text-indigo-300">Powered by BlueGecko GenAI Technology</span>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(180deg); }
          }
        `}</style>
      </div>
    </div>
  );
}
export default BlueGeckoFalcon360Logistics;